// frontend/src/components/planillas/WorkspaceLayout.jsx
// Bloques de layout presentacionales compartidos por las planillas de carga
// (Orden de Trabajo Correctiva y Planilla de Mantenimiento).
// Sin lógica de negocio: solo estructura visual.
//
// Estrategia de impresión: los TabPanel se mantienen montados y se ocultan con
// CSS solo en pantalla. En @media print el layout se aplana a una columna
// vertical para que window.print() genere un documento completo y ordenado.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ZONA_ARGENTINA } from '../../utils/dateUtils';

// CONTENEDOR RAÍZ DE LA VISTA — ocupa el alto disponible, sin scroll general.
export const WorkspacePage = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  color: ${({ theme }) => theme.colors.txtPrincipal};

  @media print {
    display: block;
    height: auto;
    min-height: 0;
    overflow: visible;
    padding: 0;
    gap: 0;
  }
`;

// BARRA SUPERIOR: volver · título · acciones.
export const WorkspaceHeader = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  @media print {
    box-shadow: none;
    border: none;
    border-bottom: 2px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 0;
    padding: 0 0 6px 0;
    margin-bottom: 8px;
  }
`;

// BOTÓN VOLVER con flecha.
export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 7px 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  i {
    font-size: 0.95rem;
  }

  /* En mobile la acción "Volver" se mueve al menú kebab (⋮) del header para
     dejarle todo el ancho al N° de OT y al equipo. */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }

  @media print {
    display: none;
  }
`;

// TEXTO DE UN BOTÓN DE ACCIÓN — se oculta en mobile para dejar solo el ícono
// (Volver / Imprimir / Guardar), así el encabezado le deja espacio al N° de OT
// y al equipo cuando se accede desde el celular (p. ej. portal de mecánicos).
export const ButtonLabel = styled.span`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

// BLOQUE DE TÍTULO (título + subtítulo).
export const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 1.05rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media print {
    font-size: 1.15rem;
    white-space: normal;
  }
`;

export const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.txtSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// PÍLDORA DE ESTADO (Abierta / Cerrada / etc.).
export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border: 1px solid ${({ theme, $tone }) => theme.colors[$tone] || theme.colors.borderDefault};
  color: ${({ theme, $tone }) => theme.colors[$tone] || theme.colors.txtSecondary};
  background: ${({ theme, $tone }) => ($tone ? `${theme.colors[$tone]}1A` : theme.colors.bgSecundario)};
`;

// CONTENEDOR DE ACCIONES (botones de la derecha del header).
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;

  /* En mobile los botones se colapsan en el menú kebab (WorkspaceActionsMenu). */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }

  @media print {
    display: none;
  }
`;

// CUERPO: columna principal + columna lateral.
// En mobile (≤ md) se apila en una sola columna: primero las pestañas
// (MainColumn), después el resumen (SideColumn) — así el mecánico ve
// todo el contenido operable sin que el panel lateral le robe espacio.
// $soloMain: en mobile/tablet vertical, cuando SideColumn no se renderiza
// (pestaña activa distinta de "Detalles"), MainColumn ocupa todo el alto
// disponible y WorkspaceBody deja de scrollear — el único scroll ahí queda
// dentro de la tabla de la pestaña (ScrollArea), igual que en desktop. Solo
// la pestaña "Detalles" muestra el resumen/cierre de la OT y necesita el
// scroll general para llegar hasta ese panel.
export const WorkspaceBody = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${({ $sideWidth }) => $sideWidth || '300px'};
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) 260px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    ${({ $soloMain }) => $soloMain
      ? 'grid-template-rows: minmax(0, 1fr); overflow: hidden;'
      : 'grid-auto-rows: auto; overflow-y: auto;'}
  }

  /* Tablet en vertical (p. ej. iPad 768–1024 px portrait): el layout de dos
     columnas de escritorio dejaba el cuerpo a ~60 % del alto con scroll interno.
     Se apila en una sola columna y se estira para ocupar todo el alto disponible,
     igual que en el celular pero conservando el tamaño de tablet. */
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) and (max-width: 1024px) and (orientation: portrait) {
    grid-template-columns: 1fr;
    ${({ $soloMain }) => $soloMain
      ? 'grid-template-rows: minmax(0, 1fr); overflow: hidden;'
      : 'grid-auto-rows: auto; overflow-y: auto;'}
  }

  @media print {
    display: block;
  }
`;

// COLUMNA PRINCIPAL: tarjeta de datos + área de pestañas.
// $flowNatural: para pestañas sin tabla (p. ej. "Detalles", cuando además se
// muestra SideColumn), en mobile/tablet vertical la columna toma el alto real
// de su contenido (sin piso artificial) para que el grid de WorkspaceBody
// reserve el espacio exacto y no solape la fila siguiente (SideColumn).
// $soloMain: cuando SideColumn no se renderiza, MainColumn ocupa el 100% real
// del alto disponible (el grid ya se lo da vía minmax(0,1fr) en WorkspaceBody)
// y el corte queda a cargo del Panel/ScrollArea internos, igual que en desktop.
export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: ${({ $flowNatural, $soloMain }) => ($soloMain ? '0' : ($flowNatural ? 'auto' : '60vh'))};
  }

  /* Tablet vertical: la columna principal se estira para llenar el alto. */
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) and (max-width: 1024px) and (orientation: portrait) {
    min-height: ${({ $flowNatural, $soloMain }) => ($soloMain ? '0' : ($flowNatural ? 'auto' : '70vh'))};
  }

  @media print {
    display: block;
  }
`;

// COLUMNA LATERAL: resumen siempre visible, con scroll propio.
// En mobile pierde el scroll propio (ya no hace falta: la página entera
// scrollea) y pasa a ocupar su alto natural debajo de MainColumn.
// $hiddenMobile: en mobile/tablet vertical, solo la pestaña "Detalles" muestra
// el resumen/cierre de la OT (ahí vive el botón de cerrar planilla y el estado
// del equipo); en las demás pestañas se oculta para que MainColumn aproveche
// toda la pantalla y la tabla tenga scroll interno sin scroll general.
export const SideColumn = styled.aside`
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: ${({ theme }) => theme.spacing.sm};
  overflow-y: auto;
  /* Scroll suave para que el auto-scroll al panel de cierre se vea bien */
  scroll-behavior: smooth;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    overflow-y: visible;
    min-height: 0;
    ${({ $hiddenMobile }) => $hiddenMobile && 'display: none;'}
  }

  /* Tablet vertical: sin scroll propio; el resumen fluye debajo del contenido. */
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) and (max-width: 1024px) and (orientation: portrait) {
    overflow-y: visible;
    min-height: 0;
    ${({ $hiddenMobile }) => $hiddenMobile && 'display: none;'}
  }

  @media print {
    display: block;
    overflow: visible;
    margin-top: 8px;
  }
`;

// TARJETA GENÉRICA (card con borde y fondo).
// $overflowVisible: evita que el dropdown del SearchableCombo quede cortado.
// $shrink: false → flex-shrink:0, el panel crece hasta su alto natural (usar en sidebar).
// $flowNatural: en mobile/tablet vertical, el panel deja de recortar y de pelear
// por el espacio del flex (pasa a flex:none) para reportar su alto real de
// contenido a MainColumn → así el grid de WorkspaceBody reserva el espacio exacto
// en vez de comprimirlo y que el contenido se derrame sobre la fila siguiente.
export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: ${({ $overflowVisible }) => $overflowVisible ? 'visible' : 'hidden'};
  ${({ $flex }) => $flex && 'flex: 1;'}
  ${({ $shrink }) => $shrink === false && 'flex-shrink: 0; min-height: unset; overflow: visible;'}

  ${({ $flowNatural, theme }) => $flowNatural && `
    @media (max-width: ${theme.breakpoints.md}) {
      flex: none;
      min-height: unset;
      overflow: visible;
    }

    @media (min-width: ${theme.breakpoints.md}) and (max-width: 1024px) and (orientation: portrait) {
      flex: none;
      min-height: unset;
      overflow: visible;
    }
  `}

  @media print {
    box-shadow: none;
    border: 1.5px solid ${({ theme }) => theme.colors.borderDefault};
    margin-bottom: 8px;
    overflow: visible;
  }
`;

// ENCABEZADO DE TARJETA.
export const PanelHead = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 7px 12px;
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};

  i {
    color: ${({ theme }) => theme.colors.primary};
    margin-right: 6px;
  }
`;

// CUERPO DE TARJETA.
export const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: ${({ $pad }) => $pad || '10px 12px'};
  ${({ $scroll }) => $scroll && 'overflow-y: auto;'}

  @media print {
    overflow: visible;
  }
`;

// BARRA DE PESTAÑAS.
// En mobile cada pestaña muestra ícono + texto y la barra se desliza con el dedo
// (swipe horizontal): así el usuario siempre lee el nombre de la pestaña. Se usa
// scroll-snap para que el paneo "enganche" prolijo en cada pestaña.
export const TabBar = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 2px;
  padding: 0 4px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { display: none; }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    scroll-snap-type: x proximity;
  }

  @media print {
    display: none;
  }
`;

// BOTÓN DE PESTAÑA.
export const TabButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  border: none;
  background: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.txtSecondary)};
  border-bottom: 3px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.transparent)};
  margin-bottom: -2px;
  padding: 8px 14px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme, $active }) =>
    $active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  i {
    font-size: 0.95rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    /* No estirar: la barra se desliza y cada pestaña conserva ícono + texto. */
    flex: 0 0 auto;
    scroll-snap-align: start;
    padding: 8px 12px;

    i {
      font-size: 1.05rem;
    }
  }
`;

// TEXTO DE LA PESTAÑA — visible siempre, también en mobile (la barra se desliza).
export const TabLabel = styled.span``;

// CONTADOR DENTRO DE UNA PESTAÑA (badge numérico).
export const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  line-height: 1;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-variant-numeric: tabular-nums;
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.borderDefault)};
  color: ${({ theme, $active }) => ($active ? theme.colors.onPrimary : theme.colors.txtSecondary)};
`;

// PANEL DE CONTENIDO DE UNA PESTAÑA — siempre montado; oculto con CSS en pantalla.
// En impresión se aplana (flex: none) para que cada panel tome su alto natural
// y las secciones se apilen en orden, en lugar de repartirse el alto disponible.
const TabPanelBase = styled.div`
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  flex: 1;
  min-height: 0;
  /* Sin esto, un hijo flex-wrap con ítems de ancho mínimo (p. ej. la fila de
     alta de un combo + inputs) puede forzar el ancho del panel más allá de
     la pantalla y arrastrar toda la columna (pestañas + resumen) de costado
     en vez de quedar contenido en su propio TableCard/ScrollArea. */
  min-width: 0;

  /* $flowNatural: pestaña sin tabla (p. ej. "Detalles") en mobile/tablet
     vertical — ver Panel/MainColumn más arriba para el porqué. */
  ${({ $flowNatural, theme }) => $flowNatural && `
    @media (max-width: ${theme.breakpoints.md}) {
      flex: none;
      min-height: unset;
    }

    @media (min-width: ${theme.breakpoints.md}) and (max-width: 1024px) and (orientation: portrait) {
      flex: none;
      min-height: unset;
    }
  `}

  @media print {
    display: flex;
    flex: 0 0 auto;
    min-height: 0;
    break-inside: auto;
  }
`;

// Panel de una pestaña. `clave` lo enlaza con su TabButton (aria-controls /
// aria-labelledby) para que un lector de pantalla anuncie a qué pestaña
// pertenece el contenido. Es opcional: los paneles que no la pasan siguen
// funcionando igual que antes, sólo sin ese vínculo.
export const TabPanel = ({ clave, ...props }) => (
  <TabPanelBase
    role="tabpanel"
    id={clave ? `panel-${clave}` : undefined}
    aria-labelledby={clave ? `pestana-${clave}` : undefined}
    // Un panel oculto va con display:none, así que no necesita aria-hidden:
    // ya está fuera del árbol de accesibilidad.
    {...props}
  />
);


// ÁREA SCROLLEABLE (para envolver tablas dentro de un panel/pestaña).
// Solo scroll vertical: si la tabla interna es más ancha que la pantalla,
// el desborde horizontal lo maneja su propio TableCard (overflow-x en mobile),
// no esta área — así no se arrastra todo el panel (pestañas + resumen) de costado.
export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  @media print {
    overflow: visible;
  }
`;

// FILA DE RESUMEN (concepto + monto).
export const SummaryRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 5px 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-bottom: 1px dashed ${({ theme }) => theme.colors.borderDefault};

  span:first-child {
    color: ${({ theme }) => theme.colors.txtSecondary};
  }
  span:last-child {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    font-variant-numeric: tabular-nums;
  }
`;

// FILA DE TOTAL DESTACADA.
export const SummaryTotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => `${theme.colors.primary}14`};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}40`};

  span:first-child {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    text-transform: uppercase;
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    letter-spacing: 0.03em;
  }
  span:last-child,
  strong:last-child {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 1.05rem;
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.primary};
    font-variant-numeric: tabular-nums;
  }
`;

// AVISO (bloqueo, advertencia) — banda informativa.
export const NoticeBar = styled.div`
  flex-shrink: 0;
  padding: 7px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ theme, $tone }) =>
    $tone === 'danger' ? `${theme.colors.danger}14` : theme.colors.warningBg};
  border: 1px solid
    ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.danger : theme.colors.warningBorder)};
  color: ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.danger : theme.colors.warningTxt)};
`;

// Helper de pestañas: renderiza la TabBar a partir de una lista declarativa.
//
// Teclado: las flechas ← → mueven de pestaña y Home/End van a los extremos,
// mientras que Tab sale de la barra hacia el contenido. Es el comportamiento
// estándar de un grupo de pestañas, y el que hace que una barra de ocho
// pestañas no cueste ocho Tabs para atravesarla: sólo la pestaña activa entra
// en el recorrido (tabIndex -1 en el resto), así Tab desde la barra cae directo
// en el primer campo del panel.
export function WorkspaceTabBar({ tabs, active, onChange }) {
  const barraRef = React.useRef(null);

  const moverFoco = (indiceDestino) => {
    const total = tabs.length;
    if (!total) return;
    // Cíclico: desde la última, → vuelve a la primera. A diferencia de una lista
    // de opciones, acá dar la vuelta es lo esperado — la barra se lee como un
    // anillo cerrado y no como un listado con principio y fin.
    const indice = (indiceDestino + total) % total;
    onChange(tabs[indice].key);
    // El foco sigue a la pestaña activada, si no el siguiente flechazo partiría
    // desde la pestaña vieja.
    const botones = barraRef.current?.querySelectorAll('[role="tab"]');
    botones?.[indice]?.focus();
  };

  const onKeyDown = (evento, indiceActual) => {
    switch (evento.key) {
      case 'ArrowRight': evento.preventDefault(); moverFoco(indiceActual + 1); break;
      case 'ArrowLeft': evento.preventDefault(); moverFoco(indiceActual - 1); break;
      case 'Home': evento.preventDefault(); moverFoco(0); break;
      case 'End': evento.preventDefault(); moverFoco(tabs.length - 1); break;
      default:
    }
  };

  return (
    <TabBar role="tablist" ref={barraRef}>
      {tabs.map((t, indice) => {
        const isActive = t.key === active;
        return (
          <TabButton
            key={t.key}
            type="button"
            role="tab"
            id={`pestana-${t.key}`}
            aria-controls={`panel-${t.key}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onKeyDown={(evento) => onKeyDown(evento, indice)}
            $active={isActive}
            onClick={() => onChange(t.key)}
          >
            {t.icon ? <i className={t.icon} /> : null}
            <TabLabel>{t.label}</TabLabel>
            {typeof t.count === 'number' ? (
              <TabCount $active={isActive}>{t.count}</TabCount>
            ) : null}
          </TabButton>
        );
      })}
    </TabBar>
  );
}

/* ----------------------------------------------------------------------------
   ACCIONES EN MÓVIL: menú kebab (⋮)
   En el celular (p. ej. portal de mecánicos) el header no tiene lugar para los
   botones Volver / Imprimir / Guardar sin tapar el N° de OT y el equipo. En
   ≤md se colapsan en un único botón ⋮ que despliega el menú. En desktop este
   bloque está oculto (se usan los botones normales del HeaderActions).
---------------------------------------------------------------------------- */

// Contenedor del kebab: SOLO visible en mobile.
const KebabWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: inline-flex;
  }

  @media print {
    display: none;
  }
`;

const KebabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;

  i { font-size: 1.2rem; }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const KebabMenu = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  z-index: 60;
`;

const KebabItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme, $tone }) =>
    $tone === 'success' ? theme.colors.success
    : $tone === 'danger' ? theme.colors.danger
    : theme.colors.txtPrincipal};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;

  &[aria-disabled='true'] {
    opacity: 0.5;
    pointer-events: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgSecundario};
  }

  i { font-size: 1rem; width: 18px; text-align: center; }
`;

// Menú de acciones para mobile. `actions` = [{ key, label, icon, tone, onClick, disabled }]
export function WorkspaceActionsMenu({ actions = [] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const visibles = actions.filter(Boolean);
  if (visibles.length === 0) return null;

  return (
    <KebabWrap ref={wrapRef}>
      <KebabButton
        type="button"
        aria-label="Acciones"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <i className="bi bi-three-dots-vertical" />
      </KebabButton>
      {open ? (
        <KebabMenu role="menu">
          {visibles.map((a) => (
            <KebabItem
              key={a.key}
              role="menuitem"
              $tone={a.tone}
              aria-disabled={a.disabled ? 'true' : 'false'}
              onClick={() => {
                if (a.disabled) return;
                setOpen(false);
                a.onClick?.();
              }}
            >
              {a.icon ? <i className={a.icon} /> : null}
              {a.label}
            </KebabItem>
          ))}
        </KebabMenu>
      ) : null}
    </KebabWrap>
  );
}

/* ----------------------------------------------------------------------------
   INDICADOR DE AUTOGUARDADO FLOTANTE (mobile)
   En desktop el estado de guardado va inline en el header. En mobile ese texto
   se metía entre Imprimir y Guardar y tapaba el N° de OT / equipo. Acá va como
   una píldora flotante fija abajo-centro, efímera, que no ocupa layout.
---------------------------------------------------------------------------- */
const FloatingWrap = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ $status }) => ($status && $status !== 'idle' ? 'flex' : 'none')};
    position: fixed;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    align-items: ${({ $status }) => ($status === 'error' ? 'flex-start' : 'center')};
    gap: 7px;
    padding: ${({ $status }) => ($status === 'error' ? '10px 14px' : '8px 16px')};
    border-radius: ${({ $status, theme }) => ($status === 'error' ? theme.borderRadius.md : '999px')};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    z-index: 70;
    pointer-events: none;
    background: ${({ theme, $status }) => ($status === 'error' ? theme.colors.dangerBg : theme.colors.surface)};
    border: 1px solid
      ${({ theme, $status }) =>
        $status === 'saved' ? theme.colors.success
        : $status === 'error' ? theme.colors.danger
        : theme.colors.borderDefault};
    color: ${({ theme, $status }) =>
      $status === 'saved' ? theme.colors.success
      : $status === 'error' ? theme.colors.danger
      : theme.colors.txtSecondary};
    width: ${({ $status }) => ($status === 'error' ? 'min(420px, calc(100vw - 32px))' : 'auto')};
    max-width: calc(100vw - 32px);
    text-align: ${({ $status }) => ($status === 'error' ? 'left' : 'center')};
    line-height: 1.35;
    white-space: ${({ $status }) => ($status === 'error' ? 'normal' : 'nowrap')};

    i { margin-top: ${({ $status }) => ($status === 'error' ? '2px' : '0')}; }
  }

  @media print {
    display: none;
  }
`;

// Toast flotante de autoguardado (solo mobile). `status`: 'idle'|'saving'|'saved'|'error'.
export function FloatingAutoSave({ status, savedAt, errorMsg }) {
  const hora = savedAt?.toLocaleTimeString('es-AR', { timeZone: ZONA_ARGENTINA, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return (
    <FloatingWrap $status={status} role="status" aria-live="polite">
      {status === 'saving' && <><i className="bi bi-arrow-repeat" /> Guardando...</>}
      {status === 'saved' && <><i className="bi bi-check-circle-fill" /> Guardado {hora}</>}
      {status === 'error' && <><i className="bi bi-exclamation-triangle-fill" /> {errorMsg || 'Error al guardar'}</>}
    </FloatingWrap>
  );
}
